<script setup lang="ts">
// Behavioural port of AI SDK Elements' Reasoning (Apache-2.0, Copyright Vercel); anatomy only.

import { computed, onBeforeUnmount, ref, toRef, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppPanelSection from '../../primitives/AppPanel/AppPanelSection.vue'
import { vScrollFade } from '../../primitives/v-scroll-fade'
import ActivityItem from './ActivityItem.vue'
import ActivityThreadHeader from './ActivityThreadHeader.vue'
import { useActivityDisclosure } from './useActivityDisclosure'
import { groupActivity, groupStatus, searchQuery } from './activity-groups'
import { shouldFollow } from './activity-follow'
import { runningStep, summarizeActivity, webSourcesOf } from './activity-summary'
import { ACTIVITY_KIND_META, type ActivityItem as ActivityItemType } from './activity-thread-types'
import { agentLabelParams } from '@/lib/chat/agent-label'

const props = withDefaults(
  defineProps<{
    items: ActivityItemType[]
    isStreaming?: boolean
    /** Externally-supplied elapsed duration (seconds); overrides auto-measurement. */
    duration?: number
    /** Starts the thread open; it is folded when absent, and only the reader's click changes it after. */
    defaultOpen?: boolean
    open?: boolean
    title?: string
  }>(),
  {
    isStreaming: false,
    defaultOpen: undefined,
    open: undefined,
    duration: undefined,
    title: undefined,
  },
)

const emit = defineEmits<{
  'update:open': [open: boolean]
}>()

const { isOpen, toggle, resolvedDuration } = useActivityDisclosure(
  toRef(() => props.isStreaming ?? false),
  {
    defaultOpen: props.defaultOpen,
    open: props.open != null ? toRef(() => props.open as boolean) : undefined,
    onOpenChange: (o) => emit('update:open', o),
    duration: toRef(() => props.duration),
  },
)

const shownSeconds = ref<number | undefined>(undefined)
let ranHere = false

watch(
  () => props.isStreaming,
  (streaming) => {
    if (!streaming) return
    ranHere = true
    shownSeconds.value = undefined
  },
  { immediate: true },
)

const runSeconds = computed(() => shownSeconds.value ?? resolvedDuration.value)

watch(runSeconds, (seconds) => {
  if (ranHere && !props.isStreaming && shownSeconds.value === undefined) {
    shownSeconds.value = seconds
  }
})

const summary = computed(() => summarizeActivity(props.items))
const entries = computed(() => groupActivity(props.items))
const webSources = computed(() => webSourcesOf(props.items))

const { t } = useI18n()

function captionOf(step: ActivityItemType): string {
  const query = step.kind === 'web-search' ? searchQuery(step) : undefined
  if (query) return t('chat.activity.searchingWebFor', { query })
  return step.labelKey
    ? t(`chat.activity.label.${step.labelKey}`, agentLabelParams(step.labelKey, step.labelParams))
    : t(ACTIVITY_KIND_META[step.kind].labelKey)
}

const activeLabel = computed(() => {
  const running = runningStep(props.items)
  return running ? captionOf(running) : undefined
})

const scroller = useTemplateRef<HTMLElement>('scroller')
let following = true
let resizeObserver: ResizeObserver | null = null

function follow(): void {
  const el = scroller.value
  if (el && props.isStreaming && following) el.scrollTop = el.scrollHeight
}

function onScroll(): void {
  if (scroller.value) following = shouldFollow(scroller.value)
}

watch(isOpen, (open) => {
  if (open) follow()
})

watch(
  () => props.isStreaming,
  (streaming) => {
    if (streaming) following = true
  },
)

watch(
  () => scroller.value?.firstElementChild ?? null,
  (list) => {
    resizeObserver?.disconnect()
    resizeObserver = null
    if (!list || typeof ResizeObserver === 'undefined') return
    resizeObserver = new ResizeObserver(follow)
    resizeObserver.observe(list)
  },
  { flush: 'post' },
)

onBeforeUnmount(() => resizeObserver?.disconnect())
</script>

<template>
  <AppPanelSection
    v-if="items.length || isStreaming"
    class="max-w-full motion-safe:animate-view-in"
    :class="isOpen ? 'w-full self-stretch' : 'w-fit self-start'"
    :open="isOpen"
    body-variant="flush"
    surface="glass"
    @update:open="toggle()"
  >
    <template #rim="{ toggle: onToggle, triggerAttrs: attrs }">
      <ActivityThreadHeader
        :open="isOpen"
        :is-streaming="!!props.isStreaming"
        :active-label="activeLabel"
        :duration="runSeconds"
        :step-count="summary.steps"
        :failed-count="summary.failed"
        :stack="summary.stack"
        :sources="webSources"
        :label="props.title"
        :trigger-attrs="attrs"
        @toggle="onToggle"
      />
    </template>

    <template v-if="items.length" #default>
      <div
        ref="scroller"
        v-scroll-fade
        class="max-h-(--activity-max-height) overflow-y-auto overscroll-contain p-8"
        @scroll.passive="onScroll"
      >
        <TransitionGroup
          tag="div"
          name="activity-step"
          role="list"
          aria-live="polite"
          class="flex flex-col"
        >
          <ActivityItem
            v-for="(entry, i) in entries"
            :key="entry.id"
            :kind="entry.type === 'step' ? entry.item.kind : 'web-search'"
            :label-key="entry.type === 'step' ? entry.item.labelKey : undefined"
            :label-params="entry.type === 'step' ? entry.item.labelParams : undefined"
            :description="entry.type === 'step' ? entry.item.description : undefined"
            :status="entry.type === 'step' ? entry.item.status : groupStatus(entry.searches)"
            :sources="entry.type === 'step' ? entry.item.sources : undefined"
            :searches="entry.type === 'searches' ? entry.searches : undefined"
            :is-last="i === entries.length - 1"
          />
        </TransitionGroup>
      </div>
    </template>
  </AppPanelSection>
</template>
